<template>
    <v-dialog :value="value" @input="$emit('input', $event)" persistent max-width="800px">
        <v-card>
            <v-card-title>
                <span class="headline">New Station Information</span>
            </v-card-title>
            <v-card-text>
                <ol class="mb-4">
                    <li>Visit <a href="https://installer.tinygs.com/" target="_blank">installer.tinygs.com</a> to create a new station.</li>
                    <li>Wait for the firmware to be loaded.</li>
                    <li>Enter your Wi-Fi credentials when prompted.</li>
                    <li>An OTP code will appear - use this code to register your station here.</li>
                </ol>

                <new-station-form
                    ref="stationForm"
                    :disabled="isCreatingStation || stationCreationCompletedSuccess"
                    @validation-changed="newStationValid = $event"
                />

                <station-map
                    ref="stationMap"
                    :disabled="isCreatingStation || stationCreationCompletedSuccess"
                    @location-updated="handleLocationUpdate"
                />
                
                <v-alert v-if="currentStationCreationMessage"
                         :type="stationCreationCompletedSuccess ? 'success' : (newStationError ? 'error' : (isCreatingStation ? 'info' : 'warning'))"
                         dense class="mt-3">
                    {{ currentStationCreationMessage }}
                </v-alert>
                
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>
                <template v-if="stationCreationCompletedSuccess">
                    <v-btn color="primary" text @click="closeDialog">
                        Close
                    </v-btn>
                </template>
                <template v-else>
                    <v-btn color="blue darken-1" text @click="handleClose">
                         {{ (isCreatingStation && pollingStationStatusIntervalId) ? 'Cancel Creation' : 'Close Dialog' }}
                    </v-btn>
                    <v-btn color="primary" text @click="createNewStation" :disabled="!newStationValid || isCreatingStation">
                        {{ isCreatingStation ? 'Creating...' : 'Create' }}
                    </v-btn>
                </template>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script setup>
// --- Composition API migration ---
// - All data properties are now refs/reactive
// - Methods are now composable functions
// - Computed properties (none in this file) would use computed()
// - Lifecycle hooks use onBeforeUnmount
// - Props and emits are defined using defineProps/defineEmits
// - Dialog and form logic, error handling, and polling are preserved
// - Template and styles are unchanged

import { ref, watch, onBeforeUnmount } from 'vue';
import axios from '../../plugins/axios';
import NewStationForm from './station/NewStationForm.vue';

// Props
const props = defineProps({
    value: {
        type: Boolean,
        required: true
    }
});

// Emits
const emit = defineEmits([
    'input',
    'station-creation-started',
    'station-creation-success',
    'station-creation-error'
]);

// Template refs
const stationForm = ref(null);
const stationMap = ref(null);

// State
const newStationValid = ref(false);
const newStationError = ref(false);
const newStationErrorMsg = ref('');
const newStationSuccess = ref(false);
const isCreatingStation = ref(false);
const pollingStationStatusIntervalId = ref(null);
const currentStationCreationMessage = ref('');
const stationCreationCompletedSuccess = ref(false);

// --- Methods ---

function handleLocationUpdate(location) {
    if (stationForm.value && stationForm.value.updateLocation) {
        stationForm.value.updateLocation(location);
    }
}

async function createNewStation() {
    if (!stationForm.value || !stationForm.value.validate()) {
        return;
    }

    if (!localStorage.userId || !localStorage.sessionToken) {
        newStationError.value = true;
        currentStationCreationMessage.value = "User not authenticated. Please log in.";
        return;
    }

    isCreatingStation.value = true;
    stationCreationCompletedSuccess.value = false;
    newStationError.value = false;
    newStationSuccess.value = false;
    newStationErrorMsg.value = '';
    currentStationCreationMessage.value = 'Initiating station registration...';

    const formData = stationForm.value.getFormData();
    const payload = {
        otp: formData.otp,
        station_name: formData.name,
        location: formData.location,
        admin_pw: formData.adminPassword,
        tz: formData.timeZone
    };

    const config = { headers: { sessionToken: localStorage.sessionToken, userId: localStorage.userId } };
    const url = `/v1/otp?userId=${localStorage.userId}`;

    try {
        await axios.post(url, payload, config);
        emit('station-creation-started');
        startStationStatusPolling();
    } catch (error) {
        const errorText = (error.response && error.response.data && (error.response.data.text || error.response.data.error)) || 'An unexpected error occurred during initiation.';
        newStationErrorMsg.value = errorText;
        currentStationCreationMessage.value = `Failed to initiate: ${errorText}`;
        newStationError.value = true;
        isCreatingStation.value = false;
    }
}

function startStationStatusPolling() {
    currentStationCreationMessage.value = 'Checking station creation status...';
    if (pollingStationStatusIntervalId.value) {
        clearInterval(pollingStationStatusIntervalId.value);
    }
    checkStationStatus();
    pollingStationStatusIntervalId.value = setInterval(checkStationStatus, 20000);
}

async function checkStationStatus() {
    if (!localStorage.userId || !localStorage.sessionToken) {
        currentStationCreationMessage.value = "Authentication details missing. Cannot check status.";
        stopStationStatusPolling();
        newStationError.value = true;
        isCreatingStation.value = false;
        return;
    }

    const config = { headers: { sessionToken: localStorage.sessionToken, userId: localStorage.userId } };
    const url = `/v1/station_created`;

    try {
        const response = await axios.get(url, config);
        const { status, text } = response.data;
        currentStationCreationMessage.value = `Status: ${status}. Details: ${text}`;

        if (status === "success") {
            stopStationStatusPolling();
            newStationSuccess.value = true;
            stationCreationCompletedSuccess.value = true;
            isCreatingStation.value = false;
            newStationError.value = false;
            emit('station-creation-success');
        } else if (status === "error") {
            stopStationStatusPolling();
            newStationError.value = true;
            newStationErrorMsg.value = text;
            isCreatingStation.value = false;
            newStationSuccess.value = false;
            emit('station-creation-error', text);
        }
    } catch (error) {
        currentStationCreationMessage.value = "Error while checking station status. Creation may have failed or been interrupted.";
        stopStationStatusPolling();
        newStationError.value = true;
        isCreatingStation.value = false;
        emit('station-creation-error', 'Error while checking station status');
    }
}

function stopStationStatusPolling() {
    if (pollingStationStatusIntervalId.value) {
        clearInterval(pollingStationStatusIntervalId.value);
        pollingStationStatusIntervalId.value = null;
    }
}

function handleClose() {
    if (isCreatingStation.value && pollingStationStatusIntervalId.value) {
        stopStationStatusPolling();
        isCreatingStation.value = false;
        newStationError.value = false;
        newStationErrorMsg.value = '';
        newStationSuccess.value = false;
        currentStationCreationMessage.value = 'Creation process cancelled. You can amend the information and try again.';
    } else {
        closeDialog();
    }
}

function closeDialog() {
    emit('input', false);
}

function cleanup() {
    if (stationForm.value && stationForm.value.reset) {
        stationForm.value.reset();
    }
    stopStationStatusPolling();
    isCreatingStation.value = false;
    currentStationCreationMessage.value = '';
    newStationError.value = false;
    newStationErrorMsg.value = '';
    newStationSuccess.value = false;
    stationCreationCompletedSuccess.value = false;
}

// --- Watchers and Lifecycle ---

// Watch dialog open/close to cleanup when closed
watch(() => props.value, (newVal) => {
    if (!newVal) {
        cleanup();
    }
});

// Cleanup polling on unmount
onBeforeUnmount(() => {
    stopStationStatusPolling();
});

// Expose to template
defineExpose({
    handleLocationUpdate,
    createNewStation,
    handleClose,
    closeDialog,
    // Expose state for template binding
    newStationValid,
    newStationError,
    newStationErrorMsg,
    newStationSuccess,
    isCreatingStation,
    pollingStationStatusIntervalId,
    currentStationCreationMessage,
    stationCreationCompletedSuccess,
    stationForm,
    stationMap
});
</script>

<style>
.v-dialog {
    margin: 0;
}
</style>